import { ArrowRightIcon, CheckIcon, CircleHelpIcon, XIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { cn } from "@/lib/utils";
import { StevieCallRecommendationTier, VoiceAIOutreachStatus } from "@/types";

// The candidacy's APR maps straight onto the badge — Stevie is never asked for
// a recommendation of her own.
const tierForAgenticScore = (
  agenticScore: number,
): StevieCallRecommendationTier | null => {
  if (agenticScore >= 5) return StevieCallRecommendationTier.STRONG_SUBMIT;
  if (agenticScore === 4) return StevieCallRecommendationTier.SUBMIT;
  if (agenticScore === 3) return StevieCallRecommendationTier.REVIEW;
  if (agenticScore >= 1) return StevieCallRecommendationTier.DO_NOT_SUBMIT;
  return null;
};

const TIER_DISPLAY: Record<
  StevieCallRecommendationTier,
  { className: string; Icon: LucideIcon; label: string }
> = {
  [StevieCallRecommendationTier.STRONG_SUBMIT]: {
    className: "bg-green-700 text-white",
    Icon: CheckIcon,
    label: "Strong Submit",
  },
  [StevieCallRecommendationTier.SUBMIT]: {
    className: "bg-primary-700 text-white",
    Icon: CheckIcon,
    label: "Submit",
  },
  [StevieCallRecommendationTier.REVIEW]: {
    className: "bg-amber-500 text-white",
    Icon: CircleHelpIcon,
    label: "Review",
  },
  [StevieCallRecommendationTier.DO_NOT_SUBMIT]: {
    className: "bg-red-600 text-white",
    Icon: XIcon,
    label: "Do Not Submit",
  },
};

export const StevieCallRecommendation = ({
  hideLearnMore = false,
  hideRecommendation = false,
  onOpenStevieCalls,
}: {
  hideLearnMore?: boolean;
  // The Submit / Do Not Submit badge is a product statement rather than a
  // display of call data, so it is still pilot-only while the link through to
  // the call is shown everywhere. Suppressing the badge must not take the link
  // with it.
  hideRecommendation?: boolean;
  onOpenStevieCalls?: () => void;
}) => {
  /* Context */
  const { candidacyId, workerId } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  });
  const callHistoryQuery = useGetCallHistory(workerId);

  /* Helpers */
  // The recommendation is about a call that happened. Without one there is
  // nothing to recommend on, however the candidacy is rated.
  const hasCompletedCall = (callHistoryQuery.data?.aiCalls ?? []).some(
    (call) =>
      call.candidacyId === candidacyId &&
      call.status === VoiceAIOutreachStatus.COMPLETED.toString(),
  );

  // The badge, or null when it is suppressed or there is nothing to show. A
  // candidacy with no rating yet has no tier to display, and that stays a badge
  // concern rather than hiding the link alongside it.
  const renderBadge = () => {
    if (hideRecommendation) return null;

    const agenticScore = candidacyInfo?.agenticScore;
    if (agenticScore == null) return null;

    const tier = tierForAgenticScore(agenticScore);
    if (!tier) return null;

    const { className, Icon, label } = TIER_DISPLAY[tier];

    return (
      <Row
        className={cn("items-center gap-1 rounded-full px-2 py-1", className)}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
        <Text className="text-xs font-semibold text-current">{label}</Text>
      </Row>
    );
  };

  /* Render */
  const render = () => {
    // Both halves are about a call that happened, so neither shows without one.
    if (!hasCompletedCall) return null;

    const badge = renderBadge();
    const learnMore = hideLearnMore ? null : (
      <Button
        className="group h-auto gap-1 p-0 text-sm font-normal text-primary-900 hover:bg-transparent"
        onClick={onOpenStevieCalls}
        type="button"
        variant="link"
      >
        Learn more
        <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </Button>
    );

    if (!badge && !learnMore) return null;

    return (
      <Row className="items-center gap-2">
        {badge}
        {learnMore}
      </Row>
    );
  };

  return render();
};
